import { useCallback, useEffect, useState } from 'react';

import { useHistory } from 'react-router-dom';

import {
  createProfile,
  getProfileList,
  deleteProfile,
} from '../../../../hooks/dynamicPricing';

export type UseProfileListPagePros = {
  isNewOpen: boolean;
  setNewOpen: (value: boolean) => void;
  isProfileListLoading: boolean;
  profileList: Profile[] | undefined;
  isNewProfileLoading: boolean;
  onCreateProfile: (profile: Profile) => void;
  errorNewProfile: boolean;
  goToListing: (form: any) => void;
  onTryToCreateAgain: () => void;
  deleteProfile: (uuid: string, editable?: boolean) => Promise<boolean>;
};

export type Profile = {
  name: string;
  uuid: string;
  chargeBoxCount: number;
  /** Definido pelo backend: perfil do próprio tenant ou de um tenant descendente */
  editable?: boolean;
};

export type ProfileListResponse = {
  error: unknown;
  dynamicPricingList: Profile[];
};

export const useProfileListPage = () => {
  const history = useHistory();

  const [isNewOpen, setNewOpen] = useState(false);

  const [isProfileListLoading, setProfileListLoading] = useState(true);

  const [profileList, setProfileList] = useState<Profile[]>();

  const [isNewProfileLoading, setNewProfileLoading] = useState(false);

  const [errorNewProfile, setErrorNewProfile] = useState(false);

  const onCreateProfile = useCallback(
    async (profile: Profile) => {
      setNewProfileLoading(true);

      try {
        const { data } = await createProfile(profile);

        if (data.error) {
          return setErrorNewProfile(true);
        }

        history.push({
          pathname: `/dynamic-pricing/${data.dynamicPricingUuid}`,
          state: {
            profileId: data.dynamicPricingUuid,
            profileName: profile.name,
            // Perfil recém-criado é sempre do tenant do usuário logado
            editable: true,
          },
        });

        setErrorNewProfile(false);
      } catch {
        setErrorNewProfile(true);
      } finally {
        setNewProfileLoading(false);

        setNewOpen(false);
      }
    },
    [setNewProfileLoading, history],
  );

  const goToListing = (form: any) => {
    setErrorNewProfile(false);

    setNewOpen(false);

    form.resetFields();
  };

  const onTryToCreateAgain = () => {
    setErrorNewProfile(false);

    setNewOpen(true);
  };

  const handleDeleteProfile = useCallback(
    async (uuid: string, editable?: boolean): Promise<boolean> => {
      // Guarda: perfil sem permissão de edição é somente leitura
      if (!editable) return false;

      try {
        setProfileListLoading(true);

        const response = await deleteProfile(uuid);

        if (response.type === 'error') {
          throw new Error(
            `Erro ao deletar documento: status ${response.status}`,
          );
        }

        setProfileList((prev) =>
          prev?.filter((profile) => profile.uuid !== uuid),
        );
        return true;
      } catch (error) {
        console.error('Error deleting the Pricing Profile:', error);
        return false;
      } finally {
        setProfileListLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        const { error, dynamicPricingList }: ProfileListResponse =
          await getProfileList();

        if (!error && dynamicPricingList?.length >= 0) {
          setProfileList(dynamicPricingList);
        }
      } catch {
        setProfileList(undefined);
      } finally {
        setProfileListLoading(false);
      }
    })();
  }, []);

  return {
    isNewOpen,
    setNewOpen,
    isProfileListLoading,
    setProfileListLoading,
    profileList,
    isNewProfileLoading,
    onCreateProfile,
    errorNewProfile,
    goToListing,
    onTryToCreateAgain,
    deleteProfile: handleDeleteProfile,
  };
};
